import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Event } from 'src/app/model/event';
import { AdminService } from 'src/app/service/admin.service';
import { WizardStep } from 'src/app/model/wizard';
import { slideOutAnimation } from 'src/app/animations/slideInOut';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';

@Component({
  selector: 'app-event-wizard',
  templateUrl: './event-wizard.component.html',
  styleUrls: ['./event-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    slideOutAnimation
  ]
})
export class AdminEventWizardComponent implements OnInit, HasUnsavedChanges {
  steps: WizardStep[];
  event: Event;

  // Anchor do `<app-popover>` do menu de opções do card (Fase 2 da migração
  // mat-menu → app-popover). `read: ElementRef` é obrigatório porque
  // `<button mat-icon-button>` é Component (sem essa flag o ViewChild
  // devolveria MatButton, sem `getBoundingClientRect`).
  @ViewChild('cardMenuBtn', { read: ElementRef })
  private cardMenuBtnRef: ElementRef<HTMLElement>;
  get cardMenuBtnEl(): HTMLElement { return this.cardMenuBtnRef?.nativeElement; }

  loading = false;
  saving = false;

  // Baseline = estado salvo (taskId → checked) capturado no load e após cada
  // save. As marcações do usuário mutam `task.checked` localmente; `isDirty()`
  // compara o estado atual contra o baseline. Modelo batch-save: nada persiste
  // até o usuário clicar "Salvar alterações" na floating-submit-bar.
  private baseline = new Map<number, boolean>();

  constructor(
    private adminService: AdminService
  ) { }

  ngOnInit(): void {
    // Pré-fetch dos steps quando o event já está em cache: dispara o request
    // do wizard IMEDIATAMENTE, sem esperar `getEvent` retornar. Se o event
    // ainda não veio, espera só ele (e usa o cache que já foi populado).
    const cachedEvent = this.adminService.selectedEvent;
    if (cachedEvent) {
      this.event = cachedEvent;
      this.getSteps(cachedEvent.id);
      return;
    }
    this.adminService.getEvent().subscribe(event => {
      this.event = event;
      this.getSteps(event.id);
    });
  }

  // HasUnsavedChanges (CanDeactivate guard).
  hasUnsavedChanges(): boolean {
    return this.isDirty();
  }

  isDirty(): boolean {
    if (!this.steps) {
      return false;
    }
    return this.steps.some(step =>
      step.tasks.some(task => this.baseline.get(task.id) !== task.checked)
    );
  }

  getSteps(event: number) {
    this.loading = true;
    this.adminService.getWizardSteps(event).subscribe(steps => {
      // Stale-while-revalidate: a 1ª emissão pode vir do cache (sessionStorage)
      // e a 2ª do backend. Aplica fresh APENAS se o user ainda não tocou em
      // nada — `isDirty()` baseado no baseline. Se já há edição em curso,
      // ignora pra não sobrescrever silenciosamente. O cache do service já
      // foi atualizado, então da próxima vez começa fresco.
      if (!this.steps || !this.isDirty()) {
        this.steps = steps;
        this.captureBaseline();
      }
      this.loading = false;
    });
  }

  private captureBaseline() {
    this.baseline.clear();
    this.steps.forEach(step =>
      step.tasks.forEach(task => this.baseline.set(task.id, task.checked))
    );
  }

  saveChanges() {
    const changed = this.steps
      .map(step => step.tasks)
      .flat(1)
      .filter(task => this.baseline.get(task.id) !== task.checked)
      .map(task => ({ id: task.id, checked: task.checked }));

    if (!changed.length) {
      return;
    }

    this.saving = true;
    this.adminService.checkWizardTask(this.event.id, changed).subscribe(
      steps => {
        // O PUT já devolve os steps atualizados; populamos o cache do service
        // com esses dados pra próximas leituras serem instantâneas.
        this.steps = steps;
        this.adminService.setWizardCache(this.event.id, steps);
        this.captureBaseline();
        this.saving = false;
      },
      () => {
        this.saving = false;
      }
    );
  }

  discardChanges() {
    // Reverte o estado local dos tasks para o baseline salvo.
    this.steps?.forEach(step =>
      step.tasks.forEach(task => task.checked = this.baseline.get(task.id) ?? task.checked)
    );
  }

  phaseIcon(stepTitle: string): string {
    switch (stepTitle) {
      case 'submission-phase': return 'upload_file';
      case 'review-phase': return 'rate_review';
      case 'discussion-phase': return 'forum';
      case 'selection-phase': return 'verified';
      default: return 'task_alt';
    }
  }

  checkAllTasks(checked: boolean) {
    // Aplica localmente — reversível via Descartar, persistido via Salvar.
    // Não precisa mais do dialog de confirmação porque a mudança é batch +
    // reversível (antes era um write direto no backend).
    this.steps?.forEach(step =>
      step.tasks.forEach(task => task.checked = checked)
    );
  }
}
